import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { EmailSettingTemplate, EmailSettings } from "schemas/email";
import { ALL } from "schemas/forms";
import { CustomEvent } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import StyledSelect from "components/Styled/Select";
import TemplateEditButton from "components/TemplateEditButton";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { sanitize } from "utils/string";
import { UNCATEGORIZED } from "utils/templates";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

import OutreachSelect from "./OutreachSelect";
import styles from "./styles";

const fieldLabelStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: "rgba(1,67,57,0.7)",
  mb: 0.5,
};

interface Props {
  templates: Template[];
  setTemplates: (templates: Template[]) => void;
  allEmailSettings?: EmailSettings[];
  setDisplayTemplateScreen?: Dispatch<SetStateAction<boolean>>;
  category?: string;
  hideEditTemplateButton?: boolean;
}

export default function TemplateScreen({
  templates,
  setTemplates,
  setDisplayTemplateScreen,
  category,
  allEmailSettings,
  hideEditTemplateButton = false,
}: Props) {
  const { emailSettings } = useContext(OrganizationUserContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);

  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const handleChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const newTemplate = allTemplates?.find((x) => x.id === value);
    if (!newTemplate) return;
    const copy = makeDeepCopy(templates);
    copy[index] = { ...newTemplate, delayDays: copy[index]?.delayDays };
    setTemplates(copy);
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = makeDeepCopy(templates);
    if (copy[index]) {
      copy[index] = { ...copy[index], delayDays: value };
      setTemplates(copy);
    }
  };

  const selectedFollowups = templates?.slice(1, templates?.length);

  const addFollowUp = () => {
    trackEvent(
      "Favorites List - Add Follow Up Button Pressed on Customization Dialog",
    );
    const first = filteredFollowUps?.[0];
    if (!first) {
      openCreateTemplate(TemplateContentType.FOLLOWUP);
      return;
    }
    setTemplates([
      ...templates,
      { ...first, delayDays: DAYS_TO_DELAY_FOLLOWUP },
    ]);
  };

  const removeFollowUp = (index: number) => {
    trackEvent("Favorites List - Remove Follow-Up on Customization Dialog");
    const copy = makeDeepCopy(templates);
    copy.splice(index, 1);
    setTemplates(copy);
  };

  const getText = (index: number) => {
    if (index === 1) return "st";
    if (index === 2) return "nd";
    if (index === 3) return "rd";
    return "th";
  };

  const openCreateTemplate = (contentType: TemplateContentType) => {
    setCurrentTemplateContentType(contentType);
    setCreateTemplateDialogOpen(true);
  };

  useRegisterSaveAsNew((newTemplate, editedId) =>
    setTemplates(templates.map((t) => (t.id === editedId ? newTemplate : t))),
  );

  const initializeEmails = () => {
    if (templates?.length > 0) return;
    if (allTemplates?.length === 0) return;

    const matchedTemplates = [];
    let presetTemplates: EmailSettingTemplate[] = [];

    const index = category
      ? allEmailSettings?.findIndex((x) => x.category === sanitize(category))
      : -1;

    if (
      category &&
      category !== ALL &&
      category !== UNCATEGORIZED &&
      index !== -1 &&
      index !== undefined &&
      allEmailSettings
    ) {
      presetTemplates = allEmailSettings[index]?.emailSettingTemplates || [];
    }

    if (presetTemplates?.length === 0) {
      presetTemplates = emailSettings?.emailSettingTemplates || [];
    }

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push({
          ...template,
          delayDays:
            matchedTemplates.length === 0
              ? undefined
              : settingTemplate.delayDays ?? DAYS_TO_DELAY_FOLLOWUP,
        });
      }
    }
    if (matchedTemplates?.length > 0) {
      setTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setTemplates([{ ...filteredOutreachTemplates[0], delayDays: undefined }]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  useEffect(() => {
    initializeEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templates?.length,
    filteredOutreachTemplates,
    emailSettings?.emailSettingTemplates?.length,
    allEmailSettings?.length,
  ]);

  return (
    <>
      {setDisplayTemplateScreen && (
        <Grid container alignItems="center" gap={1} sx={{ mb: 1 }}>
          <IconButton sx={{ fontSize: 13, color: "black" }}>
            <Box
              component="i"
              className="fa-regular fa-arrow-left"
              onClick={() => setDisplayTemplateScreen(false)}
            />
          </IconButton>
          <Typography variant="h6">Select Templates</Typography>
        </Grid>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography sx={fieldLabelStyle}>Outreach template</Typography>
        {templates[0] && (
          <Grid container alignItems="center">
            <OutreachSelect
              templates={templates}
              handleChange={handleChange}
              filteredOutreachTemplates={filteredOutreachTemplates}
              templateFromEmailSettings={templateFromEmailSettings}
              hideEditTemplateButton={hideEditTemplateButton}
              addNewOption={() =>
                openCreateTemplate(TemplateContentType.OUTREACH)
              }
            />
          </Grid>
        )}
      </Box>

      {selectedFollowups?.map((followup, index) => {
        const labelText = `${index + 1}${getText(index + 1)} follow-up`;
        return (
          <Box key={index}>
            <FollowUpDelayDivider
              days={followup?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP}
              onChange={(val) => handleDelayChange(index + 1, val)}
              isFirst={index === 0}
              align="start"
            />
            <Typography sx={fieldLabelStyle}>{labelText}</Typography>
            <Grid container alignItems="center" gap={1}>
              <Grid item xs>
                <StyledSelect
                  onChange={(e: CustomEvent) => handleChange(e, index + 1)}
                  value={followup?.id}
                  size="small"
                  fullWidth
                >
                  <MenuItem
                    onClick={() =>
                      openCreateTemplate(TemplateContentType.FOLLOWUP)
                    }
                  >
                    <Box sx={styles.createButton}>Create New</Box>
                  </MenuItem>
                  {filteredFollowUps?.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box
                        display="flex"
                        gap={1}
                        flexWrap="wrap"
                        sx={styles.templateName}
                      >
                        <Typography noWrap>
                          {template.name}
                          {template.id &&
                            templateFromEmailSettings?.includes(
                              template.id,
                            ) && (
                              <Chip
                                label="Last Used"
                                size="small"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  backgroundColor: "primary.light",
                                }}
                              />
                            )}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </StyledSelect>
              </Grid>
              {followup?.id && !hideEditTemplateButton && (
                <Grid item xs="auto">
                  <TemplateEditButton templateId={followup.id} />
                </Grid>
              )}
              <Grid item xs="auto">
                <IconButton
                  sx={{ fontSize: 13, color: "black" }}
                  onClick={() => removeFollowUp(index + 1)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}
      {templates?.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
        <Button
          disableElevation
          size="small"
          onClick={addFollowUp}
          sx={{ fontSize: 12, textTransform: "none", px: 0 }}
        >
          Add Follow Up +
        </Button>
      )}
    </>
  );
}
